import {Controller, useFieldArray, useFormContext} from 'react-hook-form';
import {useEffect, useRef} from 'react';
import {
  createDefaultDepartment,
  NetworkDetailsFormValues,
  NetworkNodeFieldPath,
} from '../../../types';
import {canDeleteDepartment} from './departmentReferenceUtils';
import {useSnackbar} from '../../../../../../../context/SnackbarContextProvider';
import AppTextInput from '../../../../../../../components/common/AppTextInput';
import AppMUIAutocomplete from '../../../../../../../components/common/AppMUIAutoComplete';
import {COLORS, ICONS} from '../../../../../../users/data';
import {SVG} from '../../../../../../../constants/assetLists';
import {useOnBoardingContext} from '../../../../../../../context/onBoarding/OnBoardingContextProvider';
import {
  getManualDepartments,
  getTemplateDepartments,
  getTemplateOrgDepartments,
} from './defaultDepartment';
import {getActiveSectionText} from '../../../../../../../utils';

type Props = {
  selectedFieldPath: NetworkNodeFieldPath;
};

function Departments({selectedFieldPath}: Props) {
  const {control, getValues, setValue, watch} =
    useFormContext<NetworkDetailsFormValues>();
  const network = watch('network') ?? [];
  const {getDefaultSelectedNode} = useOnBoardingContext();
  const defaultSelectedNode = getDefaultSelectedNode(selectedFieldPath);
  const didInitRef = useRef(false);
  const {showSnackbar} = useSnackbar();

  const {fields, append, remove, replace} = useFieldArray({
    control,
    name: `${selectedFieldPath}.departments`,
    keyName: 'id',
  });
  const isOrgMode =
    getActiveSectionText(selectedFieldPath.toString()) === 'רשת';
  useEffect(() => {
    if (didInitRef.current) return;

    if (fields.length === 0) {
      replace(
        defaultSelectedNode.department === 'manual'
          ? getManualDepartments()
          : isOrgMode
            ? getTemplateOrgDepartments()
            : getTemplateDepartments(),
      );
      setValue(`${selectedFieldPath}.roles`, []);
    }

    didInitRef.current = true;
  }, [
    defaultSelectedNode.department,
    fields.length,
    replace,
    selectedFieldPath,
    setValue,
  ]);

  const handleRemoveDepartment = (index: number) => {
    const deptId = getValues(
      `${selectedFieldPath}.departments.${index}.id` as const,
    ) as string;
    const deptName = getValues(
      `${selectedFieldPath}.departments.${index}.name` as const,
    ) as string;

    const result = canDeleteDepartment(
      network,
      selectedFieldPath,
      deptId,
      deptName,
    );

    if (!result.allowed) {
      showSnackbar({
        message: result.message,
        type: 'error',
        duration: 5000,
      });
      return;
    }

    remove(index);
  };

  const handleAddDepartment = () => {
    append(createDefaultDepartment());
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 ">
      <div className="flex flex-col gap-3">
        {fields.map((field, index) => (
          <div key={field.id} className="flex gap-3">
            <span className="text-base text-textColor font-semibold self-center w-[15px]">
              {index + 1}
            </span>

            <Controller
              control={control}
              name={`${selectedFieldPath}.departments.${index}.name`}
              render={({field, fieldState: {error}}) => (
                <div className="w-[400px]">
                  <AppTextInput
                    label="שם מחלקה"
                    placeholder="שם מחלקה"
                    className="border-none"
                    required
                    {...field}
                    error={error?.message}
                  />
                </div>
              )}
            />

            <Controller
              name={`${selectedFieldPath}.departments.${index}.icon`}
              control={control}
              render={({field: iconField, fieldState: {error}}) => (
                <div className="w-[274px]">
                  <AppMUIAutocomplete
                    // label="איקון"
                    size="small"
                    sx={{
                      '& .MuiInputBase-root': {minHeight: 48},
                    }}
                    options={ICONS}
                    getOptionLabel={o => o.id}
                    fullWidth
                    required
                    slotProps={{
                      listbox: {
                        sx: {
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          gap: 1,
                          p: 1,
                          maxHeight: 320,
                          overflowY: 'auto',
                          '& .MuiAutocomplete-option': {
                            p: '8px',
                          },
                        },
                      },
                    }}
                    renderValue={value =>
                      value && (
                        <div className="flex items-center gap-2">
                          <img
                            src={value?.icon}
                            alt="icon"
                            className="w-[20px] h-[20px] object-contain"
                          />
                          <span className="text-base text-textColor">
                            איקון
                          </span>
                        </div>
                      )
                    }
                    renderOption={(props, option) => {
                      const {key, ...optionProps} = props;

                      return (
                        <li key={key} {...optionProps}>
                          <img
                            src={option?.icon}
                            alt="icon"
                            className="w-[20px] h-[20px] object-contain"
                          />
                        </li>
                      );
                    }}
                    value={
                      ICONS.find(item => item?.id === iconField.value) || null
                    }
                    onChange={(_event, option) =>
                      iconField.onChange(option?.id ?? '')
                    }
                    onBlur={iconField.onBlur}
                    error={!!error?.message}
                    helperText={error?.message}
                  />
                </div>
              )}
            />

            <Controller
              name={`${selectedFieldPath}.departments.${index}.color`}
              control={control}
              render={({field: colorField, fieldState: {error}}) => (
                <div className="w-[274px]">
                  <AppMUIAutocomplete
                    // label="צבע"
                    size="small"
                    sx={{
                      '& .MuiInputBase-root': {minHeight: 48},
                    }}
                    options={COLORS}
                    fullWidth
                    required
                    getOptionLabel={o => o.color}
                    slotProps={{
                      listbox: {
                        sx: {
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          gap: 1,
                          p: 1,
                          maxHeight: 320,
                          overflowY: 'auto',
                          '& .MuiAutocomplete-option': {
                            p: '8px',
                            '&.Mui-focused, &[aria-selected="true"]': {
                              backgroundColor: 'transparent !important',
                            },
                          },
                        },
                      },
                    }}
                    renderValue={value =>
                      value && (
                        <div className="flex items-center gap-2">
                          <div
                            className="w-[24px] h-[24px] rounded-[3px] border-1 border-gray"
                            style={{backgroundColor: value?.color}}
                          />
                          <span className="text-base text-textColor">צבע</span>
                        </div>
                      )
                    }
                    renderOption={(props, option) => {
                      const {key, ...optionProps} = props;
                      const isSelected = colorField.value === option?.color;

                      return (
                        <li key={key} {...optionProps}>
                          <div
                            className="w-[24px] h-[24px] rounded-[3px]"
                            style={{
                              backgroundColor: option?.color,
                              border: isSelected
                                ? '2px solid #333333'
                                : '1px solid #333333',
                            }}
                          />
                        </li>
                      );
                    }}
                    value={
                      COLORS.find(item => item?.color === colorField.value) ||
                      null
                    }
                    onChange={(_event, option) =>
                      colorField.onChange(option?.color ?? '')
                    }
                    onBlur={colorField.onBlur}
                    error={!!error?.message}
                    helperText={error?.message}
                  />
                </div>
              )}
            />

            <button
              type="button"
              className="w-8 h-8 border border-gray rounded-lg flex items-center justify-center self-center shadow-sm"
              onClick={() => handleRemoveDepartment(index)}>
              <img
                src={SVG.deleteGrayIcon}
                alt="del"
                className="w-4 h-[18px] object-contain"
              />
            </button>
          </div>
        ))}

        <div>
          <button
            type="button"
            className="w-8 h-8 border border-gray rounded-lg flex items-center justify-center self-center shadow-sm"
            onClick={handleAddDepartment}>
            <img
              src={SVG.addDarkBlueIcon}
              alt="add"
              className="w-[14px] h-[14px] object-contain"
            />
          </button>
        </div>
      </div>
    </div>
  );
}

export default Departments;
